import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  Tooltip,
} from "@mui/material";
import DataGridTable from "../components/DataGridTable";
import { useAuth } from "../components/AuthContext";
import { useInstitution } from "../components/InstitutionContext";
import NoPermissionPage from "./NoPermissionPage";
import { userHasInstitutionRole } from "../utils/permissions";
import {
  fetchPostModeration,
  fetchTopicProposals,
  voteTopicProposal,
} from "../services/api";
import ModerationThreadDialog from "../components/ModerationThreadDialog";

const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

const PostModerationTable = ({ data = [], onView }) => {
  const columns = [
    { field: "thread", headerName: "Thread", flex: 1, minWidth: 200 },
    { field: "report_count", headerName: "Reports", width: 100 },
    { field: "reasons", headerName: "Reasons", flex: 1, minWidth: 160 },
    { field: "content", headerName: "Content", flex: 1, minWidth: 200 },
    { field: "status", headerName: "Status", width: 110 },
    {
      field: "action",
      headerName: "Action",
      width: 100,
      renderCell: ({ row }) => (
        <Button variant="outlined" size="small" onClick={() => onView(row.original)}>
          View
        </Button>
      ),
    },
  ];

  const rows = data.map((item) => {
    const status = item.is_removed
      ? "Removed"
      : item.is_locked
      ? "Locked"
      : item.report_count === 0
      ? "Dismissed"
      : "Reported";
    return {
      id: `${item.type}-${item.id}`,
      thread: item.thread_title || item.title,
      report_count: item.report_count || 0,
      reasons: Object.entries(item.reason_counts || {})
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ") || "—",
      content: stripHtml(item.content || item.title),
      status,
      original: item,
    };
  });

  return (
    <Box sx={{ overflowX: "auto", mb: 4 }}>
      <DataGridTable rows={rows} columns={columns} pageSize={5} sx={{ minWidth: 800 }} />
    </Box>
  );
};

const TopicProposalTable = ({ proposals = [], onVote, decisions = {}, userId }) => {
  const columns = [
    { field: "topic", headerName: "Topic", flex: 1, minWidth: 150 },
    { field: "subtopic", headerName: "Subtopic", flex: 1, minWidth: 150 },
    {
      field: "action",
      headerName: "Action",
      sortable: false,
      width: 220,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        const voted = !!decisions[row.id];
        const isOwn = row.proposed_by === userId;
        const votedTooltip = {
          title: "Voted. Awaiting final decision.",
          placement: "top",
          describeChild: true,
        };
        const ownTooltip = {
          title: "Cannot vote on your own submission.",
          placement: "top",
          describeChild: true,
        };
        const wrapVoted = (btn) =>
          voted ? <Tooltip {...votedTooltip}><span>{btn}</span></Tooltip> : btn;
        const wrapOwn = (btn) =>
          isOwn ? <Tooltip {...ownTooltip}><span>{btn}</span></Tooltip> : btn;
        const renderBtn = (btn) => wrapOwn(wrapVoted(btn));

        return (
          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
            {renderBtn(
              <Button
                variant={decisions[row.id] === "HOLD" ? "contained" : "outlined"}
                size="small"
                disabled={voted || isOwn}
                aria-label={
                  voted ? "Voted. Awaiting final decision." : isOwn ? "Cannot vote on your own submission." : undefined
                }
                onClick={() => onVote(row.id, "HOLD")}
              >
                Hold
              </Button>
            )}
            {renderBtn(
              <Button
                variant={decisions[row.id] === "ACCEPT" ? "contained" : "outlined"}
                size="small"
                color="success"
                disabled={voted || isOwn}
                aria-label={
                  voted ? "Voted. Awaiting final decision." : isOwn ? "Cannot vote on your own submission." : undefined
                }
                onClick={() => onVote(row.id, "ACCEPT")}
              >
                Accept
              </Button>
            )}
            {renderBtn(
              <Button
                variant={decisions[row.id] === "REJECT" ? "contained" : "outlined"}
                size="small"
                color="error"
                disabled={voted || isOwn}
                aria-label={
                  voted ? "Voted. Awaiting final decision." : isOwn ? "Cannot vote on your own submission." : undefined
                }
                onClick={() => onVote(row.id, "REJECT")}
              >
                Reject
              </Button>
            )}
          </Box>
        );
      },
    },
  ];

  const rows = proposals.map((p) => ({
    id: p.id,
    topic: p.topic?.name || p.topic_name,
    subtopic: p.subtopic_name,
    proposed_by: p.proposed_by?.id,
  }));

  return (
    <Box sx={{ overflowX: "auto", mb: 4 }}>
      <DataGridTable rows={rows} columns={columns} pageSize={5} sx={{ minWidth: 600 }} />
    </Box>
  );
};

const CommunityAdminDashboardPage = () => {
  const { user } = useAuth();
  const { selectedInstitution } = useInstitution();
  const isAdmin = userHasInstitutionRole(
    user,
    'CommunityAdminTeam',
    selectedInstitution
  );
  const canManageTopics =
    user?.permissions?.includes('manage_topic') &&
    user?.permissions?.includes('manage_subtopic');
  const [data, setData] = useState({ threads: [], comments: [] });
  const [proposals, setProposals] = useState([]);
  const [proposalDecisions, setProposalDecisions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [dialogPost, setDialogPost] = useState(null);
  const handleProposalVote = async (id, action) => {
    if (!canManageTopics) return;
    try {
      await voteTopicProposal(id, action);
      setProposalDecisions((prev) => ({ ...prev, [id]: action }));
      fetchData();
      setSuccessMessage('Vote submitted.');
    } catch {
      setError("Failed to submit vote.");
    }
  };

  const fetchData = async () => {
    try {
      const [postRes, propRes] = await Promise.all([
        fetchPostModeration(selectedInstitution),
        fetchTopicProposals(selectedInstitution),
      ]);
      setData(postRes || { threads: [], comments: [] });
      setProposals(propRes?.results || propRes || []);
      const decisions = {};
      (propRes?.results || propRes || []).forEach((p) => {
        if (p.my_vote) {
          decisions[p.id] = p.my_vote.action;
        }
      });
      setProposalDecisions(decisions);
    } catch (err) {
      if (err.response && err.response.status === 403) {
        setForbidden(true);
      } else {
        setError("Failed to fetch moderated posts.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && isAdmin) {
      fetchData();
    } else if (user) {
      setLoading(false);
    }
  }, [user, isAdmin, selectedInstitution]);

  if (!user) {
    return <NoPermissionPage />;
  }

  if (!isAdmin || forbidden) {
    return <NoPermissionPage showLoginLink={false} />;
  }

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const combined = [
    ...data.threads.map((t) => ({ ...t, type: "Thread" })),
    ...data.comments.map((c) => ({ ...c, type: "Comment" })),
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Community Admin Dashboard
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 3, color: "text.secondary" }}>
        Institution: {user.userprofile?.institution?.display_name || "—"}
      </Typography>
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setError("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessMessage("")}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      {canManageTopics && proposals.length > 0 && (
        <>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Proposed Topic and Subtopic
          </Typography>
          <TopicProposalTable
            proposals={proposals}
            decisions={proposalDecisions}
            userId={user.id}
            onVote={handleProposalVote}
          />
        </>
      )}
      <Typography variant="h5" sx={{ mb: 2 }}>
        Post Moderation
      </Typography>
      <PostModerationTable data={combined} onView={setDialogPost} />
      <ModerationThreadDialog
        open={!!dialogPost}
        onClose={() => setDialogPost(null)}
        post={dialogPost}
      />
    </Box>
  );
};

export default CommunityAdminDashboardPage;
